import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import React from "react"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Format a frame number with leading zeros (e.g., 42 -> "000042")
export function formatFrameNumber(frameNumber: number): string {
  return frameNumber.toString().padStart(6, '0')
}

// Format a filename for a saved frame (e.g., video_name__000042.png)
export function formatFrameFilename(frameNumber: number, videoName?: string | null): string {
  const frameStr = formatFrameNumber(frameNumber)
  
  if (videoName) {
    // Remove file extension from video name and sanitize for filename
    const baseName = videoName.replace(/\.[^/.]+$/, '') // Remove extension
    const sanitizedName = baseName.replace(/[^a-zA-Z0-9_-]/g, '_') // Replace special chars with underscore
    return `${sanitizedName}__${frameStr}.png`
  }
  
  return `frame_${frameStr}.png`
}

// Format a file size for display
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`
  } else if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  } else {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
  }
}

// Format duration in seconds to MM:SS or HH:MM:SS format
export function formatDuration(seconds: number): string {
  if (isNaN(seconds) || seconds < 0) return '00:00'
  
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  } else {
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
}

// Utility to safely extract a frame from a video element to a canvas
export function extractFrameToCanvas(
  video: HTMLVideoElement, 
  canvas: HTMLCanvasElement
): void {
  const context = canvas.getContext('2d')
  if (!context) return
  
  // Ensure canvas dimensions match video dimensions while maintaining aspect ratio
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight
  
  // Draw the current frame to the canvas
  context.drawImage(video, 0, 0, canvas.width, canvas.height)
}

// Save a canvas as a PNG file with the specified filename
export async function saveCanvasAsFile(
  canvas: HTMLCanvasElement, 
  filename: string
): Promise<void> {
  // Convert canvas to a Blob
  return new Promise<void>((resolve, reject) => {
    canvas.toBlob(async (blob) => {
      if (!blob) {
        reject(new Error('Failed to create blob from canvas'))
        return
      }
      
      try {
        // Use the simple and reliable download method
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link) // Add to DOM for Firefox compatibility
        link.click()
        document.body.removeChild(link) // Clean up
        
        // Clean up object URL
        URL.revokeObjectURL(url)
        resolve()
      } catch (error) {
        console.error('Save error:', error)
        reject(error)
      }
    }, 'image/png')
  })
}

// Video Format Information
export interface VideoFormat {
  format: string;           // Format name (e.g., 'MP4', 'WebM')
  mimeType: string;         // MIME type (e.g., 'video/mp4')
  extension: string;        // File extension (e.g., 'mp4')
  description: string;      // Human-readable description
  browserSupport: string[]; // List of browsers with good support
}

// Common video formats and their details
export const videoFormats: VideoFormat[] = [
  {
    format: 'MP4',
    mimeType: 'video/mp4',
    extension: 'mp4',
    description: 'MPEG-4 Video',
    browserSupport: ['Chrome', 'Firefox', 'Safari', 'Edge']
  },
  {
    format: 'WebM',
    mimeType: 'video/webm',
    extension: 'webm',
    description: 'WebM Video',
    browserSupport: ['Chrome', 'Firefox', 'Edge']
  },
  {
    format: 'Ogg',
    mimeType: 'video/ogg',
    extension: 'ogv',
    description: 'Ogg Video',
    browserSupport: ['Chrome', 'Firefox']
  },
  {
    format: 'AV1',
    mimeType: 'video/mp4; codecs=av01',
    extension: 'mp4',
    description: 'AV1 Video (MP4 Container)',
    browserSupport: ['Chrome', 'Firefox', 'Edge']
  },
  {
    format: 'MOV',
    mimeType: 'video/quicktime',
    extension: 'mov',
    description: 'QuickTime Movie',
    browserSupport: ['Safari']
  },
  {
    format: 'MKV',
    mimeType: 'video/x-matroska',
    extension: 'mkv',
    description: 'Matroska Video',
    browserSupport: ['Chrome', 'Edge']
  },
  {
    format: 'AVI',
    mimeType: 'video/x-msvideo',
    extension: 'avi',
    description: 'Audio Video Interleave',
    browserSupport: ['Chrome', 'Firefox']
  },
  {
    format: '3GP',
    mimeType: 'video/3gpp',
    extension: '3gp',
    description: '3GPP Multimedia',
    browserSupport: ['Chrome', 'Safari']
  },
  {
    format: 'FLV',
    mimeType: 'video/x-flv',
    extension: 'flv',
    description: 'Flash Video',
    browserSupport: []
  }
];

// Detect video format from file object
export function detectVideoFormat(file: File): VideoFormat | null {
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
  const mimeType = file.type.toLowerCase();
  
  // First try to match by MIME type
  const formatByMime = videoFormats.find(format => 
    mimeType.includes(format.mimeType.split(';')[0].toLowerCase())
  );
  
  if (formatByMime) return formatByMime;
  
  // If no MIME match, try by extension
  const formatByExtension = videoFormats.find(format => 
    format.extension.toLowerCase() === fileExtension
  );
  
  return formatByExtension || null;
}

// Check if a video format is supported by the current browser
export function isFormatSupportedByBrowser(format: VideoFormat): boolean {
  // Try to create a video element and check if it can play the format
  const video = document.createElement('video');
  
  // Check for format support using canPlayType
  const canPlay = video.canPlayType(format.mimeType);
  
  // canPlayType returns '', 'maybe', or 'probably'
  return canPlay !== '';
}

// Get a list of all supported video formats for the file input
export function getSupportedVideoFormatsForInput(): string {
  // Filter to formats likely supported by current browser
  const supported = videoFormats.filter(format => {
    const video = document.createElement('video');
    return video.canPlayType(format.mimeType) !== '';
  });
  
  // Create accept string for file input (e.g., ".mp4,.webm,video/mp4,video/webm")
  return supported
    .map(format => `.${format.extension},${format.mimeType}`)
    .join(',');
}

// Get frame rate from video metadata if available
export function getVideoFrameRate(video: HTMLVideoElement): number | null {
  // Try to access frame rate via media capabilities if supported
  if (video.getVideoPlaybackQuality) {
    try {
      const quality = video.getVideoPlaybackQuality();
      const totalFrames = quality.totalVideoFrames;
      const time = video.currentTime;
      
      // If we have enough data to estimate
      if (totalFrames > 0 && time > 0) {
        return Math.round(totalFrames / time);
      }
    } catch (e) {
      console.error('Error getting video quality information:', e);
    }
  }
  
  // Default to 30fps if we can't determine
  return 30;
}

// Extract a thumbnail from the current frame
export async function extractThumbnailFromFrame(
  videoRef: React.RefObject<HTMLVideoElement>,
  maxWidth: number = 480,
  quality: number = 0.85
): Promise<string | null> {
  const video = videoRef.current
  if (!video) return null
  
  try {
    // Create a temporary canvas
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    
    // Calculate dimensions that maintain aspect ratio
    const videoWidth = video.videoWidth
    const videoHeight = video.videoHeight
    
    // Scale dimensions down if needed
    let width = videoWidth
    let height = videoHeight
    
    if (width > maxWidth) {
      const aspectRatio = videoHeight / videoWidth
      width = maxWidth
      height = width * aspectRatio
    }
    
    // Set canvas dimensions
    canvas.width = width
    canvas.height = height
    
    // Draw the current frame
    ctx.drawImage(video, 0, 0, width, height)
    
    // Convert to data URL
    return canvas.toDataURL('image/jpeg', quality)
  } catch (error) {
    console.error('Error extracting thumbnail:', error)
    return null
  }
}

// Storage management utilities
export function getStorageQuotaInfo(): Promise<{ used: number; quota: number; usagePercentage: number }> {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    return navigator.storage.estimate().then(estimate => {
      const used = estimate.usage || 0
      const quota = estimate.quota || 0
      const usagePercentage = quota > 0 ? Math.round((used / quota) * 100) : 0
      return { used, quota, usagePercentage }
    }).catch(error => {
      console.error('Failed to get storage estimate:', error)
      return { used: 0, quota: 0, usagePercentage: 0 }
    })
  }
  return Promise.resolve({ used: 0, quota: 0, usagePercentage: 0 })
}

// Check if storage is approaching capacity (>80%)
export async function isStorageNearCapacity(): Promise<boolean> {
  const { usagePercentage } = await getStorageQuotaInfo()
  return usagePercentage > 80
}

// Format storage usage info for display
export function formatStorageInfo(used: number, quota: number): string {
  if (quota === 0) return 'Storage info unavailable'
  
  const usagePercentage = Math.round((used / quota) * 100)
  return `${formatFileSize(used)} / ${formatFileSize(quota)} (${usagePercentage}% used)`
}
